import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { getUsers } from '../../../../redux/services/authService';
import { updateSupplier } from '../../../../redux/services/suppliersService';
import { getSupplierBranches } from '../../../../redux/services/supplierAdditionalDetailsService';
import { getInternalFormTemplates } from '../../../../redux/services/internalFormsService';
import {
  BALL_IN_COURT_DEFAULT_AMOUNT_FIELD_ID,
  CreditApplicationTemplate,
} from '@dill/dill-shared';
import { getCreditApplicationTemplateLabel } from '../CreditApplicationTemplate/CONSTANTS';
import { updateUserSupplierDetails } from '../../../../redux/globalSlices/authSlice';
import { DillField, DillDropdown, DillInput, DillButton } from '../../../general/Dill';
import { openDillToast } from '../../../../utils/helpers';

type WorkflowRoute = {
  id: string;
  triggerStatus: string;
  assignTo: string;
  task: string;
  branchId: string;
  templateId: string;
  operation: string;
  amount: string;
  amountMax: string;
  amountFieldId: string;
};

type SavedRoute = {
  triggerStatus: string;
  assignTo: string;
  task?: string | null;
  branchId?: string | null;
  templateId?: string | null;
  operation?: string;
  amount?: number;
  amountMax?: number;
  amountFieldId?: string | null;
};

const OPERATION_OPTIONS = [
  { label: 'None', value: '' },
  { label: '> Greater than', value: '>' },
  { label: '>= Greater than or equal', value: '>=' },
  { label: '< Less than', value: '<' },
  { label: '<= Less than or equal', value: '<=' },
  { label: 'Between (inclusive)', value: 'between' },
];

const STATUS_TRIGGER_OPTIONS = [
  { label: 'Awaiting Signature', value: 'AWAITING_SIGNATURE' },
  { label: 'Internal Signature', value: 'INTERNAL_SIGNATURE' },
  { label: 'Application Submitted', value: 'SUBMITTED' },
  { label: 'Ready for Decision', value: 'READY_FOR_DECISION' },
  { label: 'Internally Approved', value: 'INTERNALLY_APPROVED' },
  { label: 'Internally Approved (COD)', value: 'INTERNALLY_APPROVED_COD' },
  { label: 'Internally Rejected', value: 'INTERNALLY_REJECTED' },
];

let routeUid = 0;
const nextId = () => String(++routeUid);

const emptyRoute = (): WorkflowRoute => ({
  id: nextId(),
  triggerStatus: 'AWAITING_SIGNATURE',
  assignTo: '',
  task: '',
  branchId: '',
  templateId: '',
  operation: '',
  amount: '',
  amountMax: '',
  amountFieldId: '',
});

const AMOUNT_FIELD_TYPES = ['CURRENCY', 'NUMBER'];

// Amounts are kept as plain digits ("50000") and shown with commas ("50,000").
const toAmountDigits = (value: string) => {
  const [whole, ...decimals] = value.replace(/[^\d.]/g, '').split('.');
  return decimals.length ? `${whole}.${decimals.join('').slice(0, 2)}` : whole;
};
const formatAmount = (value: string) => {
  const [whole, decimals] = value.split('.');
  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decimals != null ? `${withCommas}.${decimals}` : withCommas;
};

// A template's Currency / Number fields an amount rule can compare: the form's own (grouped ones
// included) and its signer steps' fields (e.g. a Job Info Sheet's Estimated Job $ Amount).
const getAmountFields = (template: CreditApplicationTemplate) =>
  [
    ...template.steps.flatMap((step) =>
      step.fields.flatMap((section) => [
        ...(section.unGroupedFields ?? []),
        ...(section.groupedFields ?? []).flatMap((group) => group.fields),
      ])
    ),
    ...(template.internalFormSigners ?? []).flatMap((signer) => signer.fields ?? []),
  ]
    .filter((field) => !!field.id && AMOUNT_FIELD_TYPES.includes(field.fromField))
    .map((field) => ({ id: field.id, name: field.name || field.id }));

// The editable fields of a route, as strings — what the form edits and what "dirty" compares.
const toComparableRoute = (r: SavedRoute) => ({
  triggerStatus: r.triggerStatus,
  assignTo: r.assignTo,
  task: r.task ?? '',
  branchId: r.branchId ?? '',
  templateId: r.templateId ?? '',
  operation: r.operation ?? '',
  amount: r.amount != null ? String(r.amount) : '',
  amountMax: r.amountMax != null ? String(r.amountMax) : '',
  amountFieldId: r.amountFieldId ?? '',
});

const TrashIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v6M14 11v6M9 6V4h6v2" />
  </svg>
);

const PlusIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const DeleteBtn = ({ onClick }: { onClick: () => void }) => (
  <button
    onClick={onClick}
    className="w-7 h-7 flex items-center justify-center border border-red-200 rounded-md bg-red-50 text-red-500 hover:bg-red-100 transition-colors shrink-0">
    <TrashIcon />
  </button>
);

function toLocalRoutes(saved: SavedRoute[] | null | undefined): WorkflowRoute[] {
  if (!saved?.length) return [emptyRoute()];
  return saved.map((r) => ({ id: nextId(), ...toComparableRoute(r) }));
}

function toSaveableRoutes(routes: WorkflowRoute[]): SavedRoute[] {
  return routes.map(
    ({
      triggerStatus,
      assignTo,
      task,
      branchId,
      templateId,
      operation,
      amount,
      amountMax,
      amountFieldId,
    }) => ({
      triggerStatus,
      assignTo,
      task,
      branchId,
      templateId,
      operation,
      amount: parseFloat(amount) || 0,
      ...(operation === 'between' ? { amountMax: parseFloat(amountMax) || 0 } : {}),
      amountFieldId: operation ? amountFieldId.trim() : '',
    })
  );
}

export default function CreditReviewerSettings() {
  const dispatch = useAppDispatch();
  const { user, users } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { branches: supplierBranches, loadedSupplierId: loadedSupplierBranchesId } = useAppSelector(
    (state) => state.supplierBranches
  );
  useEffect(() => {
    if (currentSupplier?.id && loadedSupplierBranchesId !== currentSupplier.id) {
      dispatch(getSupplierBranches({ supplierId: currentSupplier.id }));
    }
  }, [currentSupplier?.id, loadedSupplierBranchesId, dispatch]);

  const savedRoutes = currentSupplier?.creditApplicationSetting?.ballInCourtRouting;
  const [routes, setRoutes] = useState<WorkflowRoute[]>(() => toLocalRoutes(savedRoutes));
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setRoutes(toLocalRoutes(savedRoutes));
  }, [JSON.stringify(savedRoutes)]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentSupplier?.id) {
      dispatch(
        getUsers({
          data: {
            roles: ['CREDIT_APP_DECISION_MAKER', 'CREDIT_APP_REVIEWER'],
            // companyId: currentSupplier.id,
            linkedSupplierIds: [currentSupplier.id],
          },
        })
      );
    }
  }, [currentSupplier?.id, dispatch]);

  const usersList = useMemo(
    () => users.map((u) => ({ label: `${u.firstName} ${u.lastName}`, value: u.id })),
    [users]
  );

  const branchOptions = useMemo(
    () => [
      { label: 'Any branch', value: '' },
      ...supplierBranches.map((b) => ({
        label: b.branchName || b.branchId,
        value: b.branchId,
      })),
    ],
    [supplierBranches]
  );

  // Every template — credit / COD applications and internal / external forms — for "For form".
  const [templates, setTemplates] = useState<CreditApplicationTemplate[]>([]);
  useEffect(() => {
    if (!currentSupplier?.id) return;
    dispatch(
      getInternalFormTemplates({ supplierId: currentSupplier.id, applicationType: 'ALL' })
    ).then((result) => {
      if (result.type.includes('fulfilled')) setTemplates((result as any).payload?.data ?? []);
    });
  }, [currentSupplier?.id, dispatch]);

  const templateOptions = useMemo(
    () => [
      { label: 'Any form', value: '' },
      ...templates
        .map((template) => ({
          label: `${template.name || 'Untitled'} (${getCreditApplicationTemplateLabel(template)})`,
          value: template.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ],
    [templates]
  );

  // "Amount field" choices: the route's form's amount fields, or every form's (once each) when the
  // route is for any form. Blank keeps the default (credit amount requested).
  const getAmountFieldOptions = (route: WorkflowRoute) => {
    const fields = templates
      .filter((template) => !route.templateId || template.id === route.templateId)
      .flatMap(getAmountFields);
    const seen = new Set<string>();
    const unique = fields
      .filter((field) => !seen.has(field.id) && !!seen.add(field.id))
      .sort((a, b) => a.name.localeCompare(b.name));
    return [
      // The dropdown can't show an empty value as selected, so the default has its own id.
      { label: `Credit amount requested (default)`, value: BALL_IN_COURT_DEFAULT_AMOUNT_FIELD_ID },
      ...unique
        .filter((field) => field.id !== BALL_IN_COURT_DEFAULT_AMOUNT_FIELD_ID)
        .map((field) => ({ label: `${field.name} (${field.id})`, value: field.id })),
      // A saved field that's no longer on the form stays selectable.
      ...(route.amountFieldId &&
      route.amountFieldId !== BALL_IN_COURT_DEFAULT_AMOUNT_FIELD_ID &&
      !seen.has(route.amountFieldId)
        ? [{ label: route.amountFieldId, value: route.amountFieldId }]
        : []),
    ];
  };

  const isDirty = useMemo(() => {
    const saved = ((savedRoutes ?? []) as SavedRoute[]).map(toComparableRoute);
    const current = routes.map(({ id, ...route }) => route);
    return JSON.stringify(current) !== JSON.stringify(saved);
  }, [routes, savedRoutes]);

  const handleSave = async () => {
    if (!currentSupplier?.id || !isDirty) return;
    setIsSaving(true);
    const result = await dispatch(
      updateSupplier({
        supplierId: currentSupplier.id,
        supplierDetails: {
          creditApplicationSetting: {
            ...currentSupplier.creditApplicationSetting,
            ballInCourtRouting: toSaveableRoutes(routes),
          },
        },
      })
    );
    if (updateSupplier.fulfilled.match(result) && result.payload?.success) {
      dispatch(
        updateUserSupplierDetails({
          creditApplicationSetting: {
            ...currentSupplier.creditApplicationSetting,
            ballInCourtRouting: toSaveableRoutes(routes) as any,
          },
        })
      );
      openDillToast({ type: 'SUCCESS', message: 'Routing settings saved' });
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to save routing settings' });
    }
    setIsSaving(false);
  };

  const update = (id: string, patch: Partial<WorkflowRoute>) =>
    setRoutes((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  return (
    <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between">
        <div>
          <p className="text-sm font-semibold text-gray-900">Assignment & routing</p>
          <p className="text-xs text-gray-400 mt-0.5">
            Configure status-based ball-in-court routing and optional credit-amount assignment
            rules. Rules can be for one form: a form with its own rules for a status only uses
            those.
          </p>
        </div>
        <DillButton
          onClick={handleSave}
          label={isSaving ? 'Saving…' : 'Save'}
          variant="primary"
          isDisabled={!isDirty || isSaving}
        />
      </div>

      <div className="p-5 flex flex-col gap-2">
        {routes.map((route, i) => (
          <div key={route.id} className="flex flex-col">
            {i > 0 && (
              <div className="flex items-center gap-2 px-4 py-1">
                <div className="w-px h-4 bg-gray-200 ml-3" />
                <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">
                  then
                </span>
              </div>
            )}
            <div className="flex gap-3 items-start bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
              <div className="shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center mt-1">
                {i + 1}
              </div>
              <div className="flex flex-col gap-3 flex-1">
                <div className="flex gap-3 items-start flex-wrap">
                  <DillField label="When status reaches" className="flex-1 min-w-40">
                    <DillDropdown
                      containerClassName="h-[36px]"
                      value={route.triggerStatus}
                      onSelectChange={({ value: v }) =>
                        update(route.id, {
                          triggerStatus: v,
                          // clear amount fields when leaving READY_FOR_DECISION
                          ...(v !== 'READY_FOR_DECISION' && {
                            operation: '',
                            amount: '',
                            amountMax: '',
                            amountFieldId: '',
                          }),
                        })
                      }
                      items={STATUS_TRIGGER_OPTIONS}
                    />
                  </DillField>
                  <DillField label="For form" className="flex-1 min-w-40">
                    <DillDropdown
                      containerClassName="h-[36px]"
                      value={route.templateId}
                      onSelectChange={({ value: v }) => update(route.id, { templateId: v })}
                      items={templateOptions}
                    />
                  </DillField>
                  <DillField label="For branch" className="flex-1 min-w-40">
                    <DillDropdown
                      containerClassName="h-[36px]"
                      value={route.branchId}
                      onSelectChange={({ value: v }) => update(route.id, { branchId: v })}
                      items={branchOptions}
                    />
                  </DillField>
                  <DillField label="Assign ball-in-court to" className="flex-1 min-w-40">
                    <DillDropdown
                      containerClassName="h-[36px]"
                      value={route.assignTo}
                      onSelectChange={({ value: v }) => update(route.id, { assignTo: v })}
                      items={usersList}
                      placeholder="Select person…"
                    />
                  </DillField>
                </div>

                {route.triggerStatus === 'READY_FOR_DECISION' && (
                  <div className="flex gap-3 items-start flex-wrap">
                    <DillField label="If credit amount" className="flex-1 min-w-40">
                      <DillDropdown
                        containerClassName="h-[36px]"
                        value={route.operation}
                        onSelectChange={({ value: v }) => update(route.id, { operation: v })}
                        items={OPERATION_OPTIONS}
                      />
                    </DillField>
                    {route.operation && (
                      <DillField
                        label={route.operation === 'between' ? 'From ($)' : 'Amount ($)'}
                        className="flex-1 min-w-40">
                        <DillInput
                          value={formatAmount(route.amount)}
                          onChange={(v) => update(route.id, { amount: toAmountDigits(v) })}
                        />
                      </DillField>
                    )}
                    {route.operation === 'between' && (
                      <DillField label="To ($)" className="flex-1 min-w-40">
                        <DillInput
                          value={formatAmount(route.amountMax)}
                          onChange={(v) => update(route.id, { amountMax: toAmountDigits(v) })}
                        />
                      </DillField>
                    )}
                    {route.operation && (
                      <DillField label="Amount field" className="flex-1 min-w-40">
                        <DillDropdown
                          containerClassName="h-[36px]"
                          value={route.amountFieldId || BALL_IN_COURT_DEFAULT_AMOUNT_FIELD_ID}
                          onSelectChange={({ value: v }) =>
                            update(route.id, {
                              amountFieldId: v === BALL_IN_COURT_DEFAULT_AMOUNT_FIELD_ID ? '' : v,
                            })
                          }
                          items={getAmountFieldOptions(route)}
                        />
                      </DillField>
                    )}
                  </div>
                )}

                <DillField label="Task description (optional)">
                  <DillInput
                    value={route.task}
                    onChange={(v) => update(route.id, { task: v })}
                    placeholder="e.g. Put together credit package"
                  />
                </DillField>
              </div>
              <div className="mt-1 shrink-0">
                <DeleteBtn
                  onClick={() => setRoutes((prev) => prev.filter((x) => x.id !== route.id))}
                />
              </div>
            </div>
          </div>
        ))}
        <DillButton
          variant="secondary"
          label="Add routing step"
          icon={<PlusIcon />}
          onClick={() => setRoutes((prev) => [...prev, emptyRoute()])}
        />
      </div>
    </div>
  );
}
